"use client";

import { useState } from "react";
import Image from "next/image";
import { ArrowLeftRight, History, X } from "lucide-react";
import { useGenerations, type GenerationWithVehicle } from "@/hooks/use-generations";
import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { trackFreeExit } from "@/lib/analytics";
import { useCredits } from "@/hooks/use-credits";
import {
  Drawer,
  DrawerClose,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerTrigger,
} from "@/components/ui/drawer";

interface GenerationHistoryDrawerProps {
  className?: string;
  selectedId?: string | null;
  onSelect?: (generation: GenerationWithVehicle) => void;
  children?: React.ReactNode;
  onCompareFreePreviews?: () => void;
}

export function GenerationHistoryDrawer({ 
  className, 
  selectedId, 
  onSelect,
  children,
  onCompareFreePreviews,
}: GenerationHistoryDrawerProps) {
  const [open, setOpen] = useState(false);
  const { data, isLoading } = useGenerations();
  const { data: credits } = useCredits();
  const hasPurchasedAnyPack = credits?.hasPurchasedAnyPack ?? false;

  const generations = data?.generations || [];
  
  // Track comparison view for free users
  const handleOpenChange = (isOpen: boolean) => {
    setOpen(isOpen);
    if (isOpen && !hasPurchasedAnyPack) {
      // Check if user has any free preview generations
      const hasFreePreview = generations.some(g => g.isFreePreview && g.status === "completed");
      if (hasFreePreview) {
        trackFreeExit({
          exitPoint: "comparison_view",
        });
      }
    }
  };
  const completedGenerations = generations
    .filter((g) => g.status === "completed" && g.resultImageUrl)
    .sort((a, b) => {
      const dateA = a.completedAt ? new Date(a.completedAt).getTime() : new Date(a.createdAt).getTime();
      const dateB = b.completedAt ? new Date(b.completedAt).getTime() : new Date(b.createdAt).getTime();
      return dateB - dateA;
    });

  const handleSelect = (gen: GenerationWithVehicle) => {
    onSelect?.(gen);
    setOpen(false);
  };

  if (isLoading || completedGenerations.length === 0) {
    return null;
  }

  return (
    <Drawer open={open} onOpenChange={handleOpenChange}>
      <DrawerTrigger asChild>
        {children || (
          <Button
            variant="outline"
            size="sm"
            className={cn(
              "gap-2.5 rounded-xl",
              "border-2 border-primary bg-transparent hover:bg-primary/10",
              "sm:border-white/10 sm:bg-white/5 sm:hover:bg-white/10",
              "h-12 sm:h-9 px-5 sm:px-3 py-3 sm:py-2",
              className
            )}
          >
            <History className="w-5 h-5 sm:w-3.5 sm:h-3.5" />
            <span className="text-base sm:text-xs font-semibold">My previews</span>
            <span className="flex items-center justify-center w-7 h-7 sm:w-5 sm:h-5 rounded-full bg-primary text-primary-foreground text-sm sm:text-xs font-semibold">
              {completedGenerations.length}
            </span>
          </Button>
        )}
      </DrawerTrigger>
      
      <DrawerContent className="max-h-[85vh] max-w-4xl mx-auto flex flex-col">
        <DrawerHeader className="border-b border-white/5 pb-4">
          <div className="flex items-center justify-between">
            <div>
              <DrawerTitle className="text-lg">My previews</DrawerTitle>
              <p className="mt-0.5 text-xs text-zinc-500">Reopen a design to compare it with your next idea.</p>
            </div>
            <DrawerClose asChild>
              <Button variant="ghost" size="icon" className="h-8 w-8" aria-label="Close preview history">
                <X className="h-4 w-4" />
              </Button>
            </DrawerClose>
          </div>         
        </DrawerHeader>

        {onCompareFreePreviews ? (
          <div className="border-b border-white/5 px-4 py-3">
            <Button
              variant="outline"
              className="w-full justify-center gap-2 border-blue-500/30 bg-blue-500/10 text-blue-200 hover:bg-blue-500/15"
              onClick={() => {
                setOpen(false);
                onCompareFreePreviews();
              }}
            >
              <ArrowLeftRight className="h-4 w-4" />
              Compare my two free previews
            </Button>
          </div>
        ) : null}
        
        <div className="flex-1 overflow-y-auto px-4 py-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 pb-8">
            {completedGenerations.map((gen) => {
              const isSelected = selectedId === gen.id;
              const date = gen.completedAt ? new Date(gen.completedAt) : new Date(gen.createdAt);
              const wheelName = gen.userRim?.name || gen.rim?.name || "Saved wheel";

              return (
                <button
                  key={gen.id}
                  onClick={() => handleSelect(gen)}
                  className={cn(
                    "w-full rounded-xl overflow-hidden bg-card border transition-all flex flex-col",
                    "hover:border-primary/50 active:scale-[0.99]",
                    isSelected
                      ? "border-primary ring-2 ring-primary/20"
                      : "border-white/5"
                  )}
                >
                  {/* Large Image */}
                  <div className="relative aspect-video w-full">
                    <Image
                      src={gen.resultImageUrl!}
                      alt={`Preview from ${date.toLocaleDateString()}`}
                      fill
                      className="object-cover"
                      sizes="(max-width: 640px) 100vw, (max-width: 1024px) 50vw, 33vw"
                    />
                    
                    {/* Free Preview Badge */}
                    {gen.isFreePreview && (
                      <div className={cn(
                        "absolute top-3 right-3 px-2.5 py-1 rounded-full text-xs font-medium",
                        gen.isUnlocked
                          ? "bg-emerald-500/90 text-white"
                          : "bg-amber-500/90 text-white"
                      )}>
                        {gen.isUnlocked ? "Unlocked" : "Preview"}
                      </div>
                    )}
                    
                    {/* Selected Indicator */}
                    {isSelected && (
                      <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-primary text-primary-foreground text-xs font-medium">
                        Selected
                      </div>
                    )}
                  </div>
                  
                  {/* The wheel is the memorable label; the date is supporting context. */}
                  <div className="p-3 text-left bg-card/50 flex-1 w-full border-t border-white/5 flex items-center justify-between gap-2">
                    <div className="flex-1 min-w-0">
                      <p className="truncate text-sm font-medium text-foreground">{wheelName}</p>
                      <p className="mt-0.5 text-xs text-muted-foreground">
                        {date.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" })}
                      </p>
                    </div>

                    {/* Wheel thumbnail */}
                    {(gen.rim || gen.userRim) && (
                      <div className="relative w-10 h-10 rounded-md overflow-hidden bg-white/5 shrink-0 border border-white/10">
                        <Image
                          src={gen.userRim?.imageUrl || gen.rim?.imageUrl || ""}
                          alt={gen.userRim?.name || gen.rim?.name || "Wheel"}
                          fill
                          sizes="40px"
                          className="object-cover"
                        />
                      </div>
                    )}

                    {/* Deleted wheel indicator */}
                    {!gen.rim && !gen.userRim && (
                      <div className="w-10 h-10 rounded-md bg-white/5 shrink-0 border border-white/10 flex items-center justify-center">
                        <span className="text-xs">❌</span>
                      </div>
                    )}
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      </DrawerContent>
    </Drawer>
  );
}
